import { Stage } from '@eventbrite/webapp-constants';

const REPORT_PROXY_HOSTS: any = {
  [Stage.LOCAL]: '',
  [Stage.DEV]: 'https://iet56g5jki-vpce-02f9076d6190f253b.execute-api.us-east-1.amazonaws.com/prod',
  [Stage.TEST]:
    'https://a9i8vtvcvb-vpce-01a86a586e993f3b6.execute-api.us-east-1.amazonaws.com/prod',
  [Stage.PROD]:
    'https://3ph8pqx2m4-vpce-04e1d8674e4604fa7.execute-api.us-east-1.amazonaws.com/prod',
};
export const getReportProxyURL = (stage: Stage): string => {
  return REPORT_PROXY_HOSTS[stage] as string;
};

export function getStage() {
  return (process.env.STAGE ?? Stage.TEST) as Stage;
}

export function forceDevStageInDevelopmentEnvironment() {
  if (getStage() === Stage.TEST && process.env.NODE_ENV === 'development') {
    return Stage.DEV;
  }
  return getStage();
}

// Domains for different environments when constructing external links
const EXTERNAL_LINK_DOMAINS: Record<Stage, string> = {
  [Stage.LOCAL]: 'https://www.evbqa.com',
  [Stage.DEV]: 'https://www.evbqa.com',
  [Stage.TEST]: 'https://www.evbqa.com',
  [Stage.PROD]: '', // Empty string for production as we use relative URLs
};

// Domains for legacy API calls (always need full domain)
const LEGACY_DOMAINS: Record<Stage, string> = {
  [Stage.LOCAL]: 'https://www.evbqa.com',
  [Stage.DEV]: 'https://www.evbqa.com',
  [Stage.TEST]: 'https://www.evbqa.com',
  [Stage.PROD]: 'https://www.eventbrite.com',
};

/**
 * Returns the appropriate domain for legacy API calls based on the current stage
 * Always returns a full domain URL, unlike getExternalURL which uses relative URLs in production
 *
 * @returns The legacy domain URL for the current environment
 */
export function getLegacyDomain(): string {
  const stage = getStage();
  return LEGACY_DOMAINS[stage];
}

/**
 * Constructs an external URL based on the current environment.
 * Works on both server-side and client-side.
 *
 * Server-side: Uses STAGE environment variable to determine the domain
 * Client-side: Uses window.location.hostname to detect environment
 *
 * Production: Returns relative URL (no domain prefix) to avoid hydration mismatches
 * Non-production: Returns full URL with QA domain
 *
 * @param relativePath - The relative path (should start with '/')
 * @returns The properly constructed URL for the current environment
 */
export function getExternalURL(relativePath: string): string {
  // Client-side: detect environment from hostname
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;
    // Production: hostname contains 'eventbrite' but not 'evbqa'
    // This covers all TLDs: .com, .ca, .co.uk, etc.
    const isProduction = hostname.includes('eventbrite') && !hostname.includes('evbqa');

    // Production: use relative URLs (matches server behavior, avoids hydration mismatch)
    // Non-production (dev/QA): use QA domain for external links
    return isProduction ? relativePath : `https://www.evbqa.com${relativePath}`;
  }

  // Server-side: use STAGE environment variable
  const stage = getStage();
  const domain = EXTERNAL_LINK_DOMAINS[stage];

  return `${domain}${relativePath}`;
}

/**
 * returns if the window is defined, used for determining browser vs server
 */
export const HAS_WINDOW = typeof window !== 'undefined';

export const getDomain = () => {
  if (typeof window === 'undefined') {
    return null;
  }

  return window.location.hostname.replace(/^www\./, '');
};

// Export navigation utilities
export * from './navigation';

// Export validation utilities
export * from './validation';
